import React, { createContext, useContext, useState, useEffect } from 'react';
import cookieService, { COOKIE_NAMES } from '../utils/cookieService';
import notificationService from '../utils/notificationService';
import { refreshAuthToken } from '../services/tokenRefreshService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);
  const [refreshTimer, setRefreshTimer] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  const [refreshQueue, setRefreshQueue] = useState([]);
  const [logoutWarningTimer, setLogoutWarningTimer] = useState(null);
  const [gracefulLogoutScheduled, setGracefulLogoutScheduled] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false); // New state to prevent race conditions

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Cleanup refresh timer on unmount and state changes
  useEffect(() => {
    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        setRefreshTimer(null);
      }
    };
  }, [refreshTimer]);

  // Additional cleanup on window unload to prevent memory leaks
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, [refreshTimer]);

  // Schedule proactive token refresh with atomic timer operations
  const scheduleTokenRefresh = (tokenExpiryTime) => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    if (!tokenExpiryTime) {
      if (isDevMode) console.log('⚠️ No token expiry time provided, skipping refresh schedule');
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = tokenExpiryTime - currentTime;
    
    // Refresh 5 minutes (300 seconds) before expiry, but not if token expires in less than 10 minutes
    const refreshBuffer = 300; // 5 minutes
    const minRefreshTime = 600; // 10 minutes
    
    if (timeUntilExpiry <= minRefreshTime) {
      if (isDevMode) console.log('⚠️ Token expires soon, will refresh on next API call');
      return;
    }

    const refreshDelay = (timeUntilExpiry - refreshBuffer) * 1000; // Convert to milliseconds
    
    if (isDevMode) {
      const refreshTime = new Date(Date.now() + refreshDelay);
      console.log(`⏰ Token refresh scheduled for: ${refreshTime.toLocaleTimeString()}`);
    }

    // Atomic timer replacement to prevent race conditions
    setRefreshTimer(currentTimer => {
      // Clear existing timer if any
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
      
      // Create and return new timer
      return setTimeout(async () => {
        if (isDevMode) console.log('⏰ Proactive token refresh triggered');
        await performTokenRefresh();
      }, refreshDelay);
    });
  };

  // Get token expiry from current token
  const getTokenExpiry = () => {
    try {
      const storedUser = localStorage.getItem('test_auth_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        if (userData.idToken) {
          // Decode JWT to get expiry
          const payload = JSON.parse(atob(userData.idToken.split('.')[1]));
          return payload.exp;
        }
      }
      
      // Fallback to cookie service
      return cookieService.getCurrentUserFromToken()?.exp;
    } catch (error) {
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.warn('⚠️ Could not determine token expiry:', error);
      }
      return null;
    }
  };

  // Perform proactive token refresh with retry logic
  const performTokenRefresh = async (attempt = 0) => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    const maxRetries = 3;
    
    if (isRefreshing && attempt === 0) {
      if (isDevMode) console.log('⏳ Token refresh already in progress, skipping');
      return false;
    }
    
    setIsRefreshing(true);
    
    try {
      if (isDevMode) {
        const attemptMsg = attempt > 0 ? ` (attempt ${attempt + 1}/${maxRetries + 1})` : '';
        console.log(`🔄 Starting proactive token refresh${attemptMsg}...`);
      }
      
      const refreshSuccess = await refreshTokenWithRetry();
      
      if (refreshSuccess) {
        if (isDevMode) console.log('✅ Proactive token refresh successful');
        setRefreshAttempts(0); // Reset retry counter on success
        
        // Schedule next refresh based on new token expiry
        try {
          const newTokenExpiry = getTokenExpiry();
          if (newTokenExpiry) {
            scheduleTokenRefresh(newTokenExpiry);
          } else {
            if (isDevMode) console.warn('⚠️ Could not get new token expiry after refresh, scheduling skipped');
          }
        } catch (error) {
          if (isDevMode) console.error('❌ Error scheduling next refresh after successful token refresh:', error);
          // Continue without scheduling - reactive refresh will handle future needs
        }
        
        return true;
      } else {
        // Implement retry logic with exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          if (isDevMode) console.warn(`⚠️ Token refresh failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          
          setTimeout(async () => {
            await performTokenRefresh(attempt + 1);
          }, delay);
          
          return false;
        } else {
          if (isDevMode) console.error('❌ Token refresh failed after all retry attempts');
          setRefreshAttempts(0); // Reset counter
          
          // Show user notification and schedule graceful logout
          handleRefreshFailure();
          return false;
        }
      }
    } catch (error) {
      if (isDevMode) console.error('❌ Proactive token refresh error:', error);
      
      // Retry on network errors
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000;
        if (isDevMode) console.warn(`⚠️ Network error, retrying in ${delay}ms...`);
        
        setTimeout(async () => {
          await performTokenRefresh(attempt + 1);
        }, delay);
        
        return false;
      }
      
      return false;
    } finally {
      if (attempt === 0 || attempt >= maxRetries) {
        setIsRefreshing(false);
      }
    }
  };

  // Enhanced refresh token function with queue management
  const refreshTokenWithRetry = async () => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    try {
      const refreshTokenCookie = cookieService.getAuthCookie(COOKIE_NAMES.REFRESH_TOKEN);
      
      if (!refreshTokenCookie) {
        if (isDevMode) {
          console.warn('No refresh token available');
        }
        return false;
      }

      // Try the tick endpoint instead of refresh-with-cookies
      const response = await fetch(
        `${process.env.REACT_APP_PROXY_SERVER_URL || 'https://api.aristai.io'}/api/auth/tick`,
        {
          method: 'GET',
          credentials: 'include',
          cache: 'no-cache',
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.isLogged === true) {
          // Server already updated HttpOnly cookies, just update local state
          const newIdToken = data.idToken;
          if (newIdToken) {
            setIdToken(newIdToken);
          }
          if (isDevMode) {
            console.log('✅ Token refreshed successfully via tick endpoint');
          }
          return true;
        }
      }
      
      if (isDevMode) {
        console.warn('Token refresh failed');
      }
      return false;
    } catch (error) {
      if (isDevMode) {
        console.error('Token refresh error:', error);
      }
      return false;
    }
  };

  // Handle refresh failure with graceful degradation
  const handleRefreshFailure = () => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    if (gracefulLogoutScheduled) {
      if (isDevMode) console.log('⚠️ Graceful logout already scheduled');
      return;
    }

    setGracefulLogoutScheduled(true);

    // Show user notification with option to retry
    const notificationId = notificationService.showTokenRefreshFailed(true);
    
    // Schedule logout warning
    const warningTimer = setTimeout(() => {
      // Show 30-second warning
      const warningId = notificationService.showLogoutWarning(30);
      
      // Final logout after 30 seconds unless cancelled
      const finalTimer = setTimeout(() => {
        if (gracefulLogoutScheduled) {
          if (isDevMode) console.log('🔄 Graceful logout proceeding after final warning');
          logout();
        }
      }, 30000);
      
      // Store timer for potential cancellation
      setLogoutWarningTimer(finalTimer);
      
    }, 120000); // 2 minutes before logout warning
    
    setLogoutWarningTimer(warningTimer);
    
    if (isDevMode) console.log('⚠️ Graceful logout scheduled in 2.5 minutes');
  };

  // Cancel graceful logout (when user manually refreshes)
  const cancelGracefulLogout = () => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    if (logoutWarningTimer) {
      clearTimeout(logoutWarningTimer);
      setLogoutWarningTimer(null);
    }
    
    setGracefulLogoutScheduled(false);
    notificationService.clearAll();
    
    if (isDevMode) console.log('✅ Graceful logout cancelled');
  };

  // Manual refresh token function for user-triggered refresh
  const manualRefreshToken = async () => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    if (isDevMode) console.log('🔄 Manual token refresh requested');
    
    // Cancel any pending logout
    cancelGracefulLogout();
    
    try {
      const success = await refreshToken();
      
      if (success) {
        notificationService.showRefreshSuccess();
        if (isDevMode) console.log('✅ Manual token refresh successful');
        return true;
      } else {
        notificationService.showTokenRefreshFailed(false);
        if (isDevMode) console.error('❌ Manual token refresh failed');
        return false;
      }
    } catch (error) {
      if (isDevMode) console.error('❌ Manual token refresh error:', error);
      notificationService.showTokenRefreshFailed(false);
      return false;
    }
  };

  // Check if token is expiring soon and show warning
  const checkTokenExpiry = () => {
    try {
      const tokenExpiry = getTokenExpiry();
      if (!tokenExpiry) return;
      
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenExpiry - currentTime;
      
      // Show warning if token expires in less than 5 minutes
      if (timeUntilExpiry > 0 && timeUntilExpiry <= 300) {
        notificationService.showTokenExpiring(timeUntilExpiry);
      }
    } catch (error) {
      // Ignore errors in token expiry checking
    }
  };

  const checkAuthStatus = async () => {
    // Check if in dev mode
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    // Set checking auth flag to prevent race conditions
    setIsCheckingAuth(true);
    
    try {

      // OPTION C: Simplified API-only authentication
      // Use server-side cookie fallback - no need to read cookies client-side
      if (isDevMode) {
        console.log('🔄 Checking authentication via ProxyServer API with cookie fallback...');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_PROXY_SERVER_URL || 'https://api.aristai.io'}/api/auth/user`,
        {
          method: 'GET',
          credentials: 'include', // Server will read HttpOnly cookies as fallback
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-cache',
        }
      );

      if (response.ok) {
        const userData = await response.json();
        if (isDevMode) {
          console.log('✅ Authentication successful, user data:', userData);
        }
        setUser(userData);
        setIdToken('authenticated'); // We don't need the actual token value
        
        // Schedule proactive token refresh for production seamless experience
        try {
          const tokenExpiry = getTokenExpiry();
          if (tokenExpiry) {
            scheduleTokenRefresh(tokenExpiry);
          }
        } catch (error) {
          if (isDevMode) console.error('❌ Error scheduling initial token refresh:', error);
          // Continue without scheduling - reactive refresh will handle future needs
        }
      } else if (response.status === 401) {
        // Try to refresh the token before giving up
        if (isDevMode) {
          console.log('⚠️ Initial auth check returned 401, attempting token refresh...');
        }
        
        const refreshSuccess = await refreshAuthToken();
        
        if (refreshSuccess) {
          if (isDevMode) {
            console.log('✅ Token refreshed, retrying user endpoint...');
          }
          
          // Retry the user endpoint
          const retryResponse = await fetch(
            `${process.env.REACT_APP_PROXY_SERVER_URL || 'https://api.aristai.io'}/api/auth/user`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              cache: 'no-cache',
            }
          );
          
          if (retryResponse.ok) {
            const userData = await retryResponse.json();
            if (isDevMode) {
              console.log('✅ Authentication successful after refresh, user data:', userData);
            }
            setUser(userData);
            setIdToken('authenticated');
            
            // Schedule proactive token refresh
            try {
              const tokenExpiry = getTokenExpiry();
              if (tokenExpiry) {
                scheduleTokenRefresh(tokenExpiry);
              }
            } catch (error) {
              if (isDevMode) console.error('❌ Error scheduling token refresh after retry:', error);
            }
            
            setLoading(false);
            setIsCheckingAuth(false);
            return;
          }
        }
        
        // If refresh failed or retry failed, continue with original 401 handling
        if (isDevMode) {
          console.log('⚠️ Authentication failed (401), user not logged in');
        }
        
        // In dev mode, check for stored user data even on 401
        if (isDevMode) {
          console.log('🚀 DEV MODE: 401 response, checking for stored user...');
          
          // Check for stored user data from localStorage
          try {
            const storedUser = localStorage.getItem('test_auth_user');
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              console.log('✅ Found stored user data after 401, setting auth state:', userData);
              setUser(userData);
              setIdToken(userData.idToken || 'stored-token');
              
              // Schedule token refresh for stored user too
              try {
                const tokenExpiry = getTokenExpiry();
                if (tokenExpiry) {
                  scheduleTokenRefresh(tokenExpiry);
                }
              } catch (error) {
                if (isDevMode) console.error('❌ Error scheduling token refresh for stored user:', error);
              }
              
              setLoading(false);
              return;
            }
          } catch (error) {
            console.warn('⚠️ Error reading stored user data:', error);
          }
        }
        
        setUser(null);
        setIdToken(null);
      } else {
        if (isDevMode) {
          console.warn('⚠️ Unexpected response from auth endpoint:', response.status);
        }
        
        // In dev mode, allow access even if auth endpoint fails
        if (isDevMode) {
          console.log('🚀 DEV MODE: Auth endpoint failed, checking for mock user...');
          
          // Check for mock user set by testAuthDirectly()
          if (window.mockAuthUser) {
            console.log('✅ Found window mock user, setting auth state:', window.mockAuthUser);
            setUser(window.mockAuthUser);
            setIdToken(window.mockAuthUser.idToken || 'mock-token-123');
            setLoading(false);
            setIsCheckingAuth(false);
            return;
          }
          
          // Check for stored user data from localStorage
          try {
            const storedUser = localStorage.getItem('test_auth_user');
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              console.log('✅ Found stored user data, setting auth state:', userData);
              setUser(userData);
              setIdToken(userData.idToken || 'stored-token');
              setLoading(false);
              return;
            }
          } catch (error) {
            console.warn('⚠️ Error reading stored user data:', error);
          }
          
          console.log('🚀 DEV MODE: Allowing access without authentication for testing');
          setUser(null);
          setIdToken(null);
        } else {
          setUser(null);
          setIdToken(null);
        }
      }
    } catch (error) {
      if (isDevMode) {
        console.error('❌ Auth check error:', error);
      }
      
      // In dev mode, allow access even on network errors
      if (isDevMode) {
        console.log('🚀 DEV MODE: Network error, checking for mock user...');
        
        if (window.mockAuthUser) {
          console.log('✅ Found window mock user, setting auth state:', window.mockAuthUser);
          setUser(window.mockAuthUser);
          setIdToken(window.mockAuthUser.idToken || 'mock-token-123');
          setLoading(false);
          return;
        }
        
        // Check for stored user data from localStorage
        try {
          const storedUser = localStorage.getItem('test_auth_user');
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            console.log('✅ Found stored user data after network error, setting auth state:', userData);
            setUser(userData);
            setIdToken(userData.idToken || 'stored-token');
            setLoading(false);
            setIsCheckingAuth(false);
            return;
          }
        } catch (error) {
          console.warn('⚠️ Error reading stored user data:', error);
        }
        
        console.log('🚀 DEV MODE: Allowing access without authentication for testing');
      }
      
      setUser(null);
      setIdToken(null);
    } finally {
      setLoading(false);
      setIsCheckingAuth(false);
    }
  };

  // Main refresh token function with queue management and race condition protection
  const refreshToken = async () => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    // Create a promise for this refresh request
    return new Promise((resolve) => {
      // Add to queue
      setRefreshQueue(currentQueue => {
        const newQueue = [...currentQueue, { resolve, timestamp: Date.now() }];
        
        // If this is the first request or no refresh in progress, start processing
        if (!isRefreshing) {
          processRefreshQueue(newQueue);
        }
        
        return newQueue;
      });
    });
  };

  // Process the refresh queue atomically
  const processRefreshQueue = async (queue) => {
    const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
    
    if (isRefreshing || queue.length === 0) {
      return;
    }
    
    setIsRefreshing(true);
    
    try {
      if (isDevMode) console.log(`🔄 Processing refresh queue with ${queue.length} requests...`);
      
      const result = await refreshTokenWithRetry();
      
      // Resolve all queued requests with the same result
      queue.forEach(({ resolve }) => resolve(result));
      
      // Clear the queue
      setRefreshQueue([]);
      
      if (isDevMode) console.log(`✅ Refresh queue processed, ${queue.length} requests resolved`);
      
    } catch (error) {
      if (isDevMode) console.error('❌ Error processing refresh queue:', error);
      
      // Resolve all queued requests with failure
      queue.forEach(({ resolve }) => resolve(false));
      setRefreshQueue([]);
      
    } finally {
      setIsRefreshing(false);
    }
  };

  const logout = async () => {
    try {
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.log('🔄 Logging out...');
      }
      
      // Call ProxyServer signout endpoint to clear HttpOnly cookies server-side
      const response = await fetch(
        `${process.env.REACT_APP_PROXY_SERVER_URL || 'https://api.aristai.io'}/api/auth/signout`,
        {
          method: 'GET',
          credentials: 'include', // Send cookies to be cleared
          cache: 'no-cache',
        }
      );
      
      if (response.ok) {
        if (process.env.REACT_APP_DEV_MODE === 'true') {
          console.log('✅ Server-side logout successful');
        }
      } else {
        if (process.env.REACT_APP_DEV_MODE === 'true') {
          console.warn('⚠️ Server-side logout failed, but continuing...');
        }
      }
    } catch (error) {
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.error('❌ Logout error:', error);
      }
    }

    // Clear client-side cookies (for non-HttpOnly cookies)
    try {
      // Use cookieService to clear auth cookies
      cookieService.clearIdTokenCookie();
      cookieService.clearRefreshTokenCookie();
      
      // Also manually clear all auth cookies
      const cookiesToClear = ['_aristai_session', '_aristai_id', '_aristai_refresh_token', '_aristai_server_refresh_token', '_aristai_auth'];
      
      cookiesToClear.forEach(cookieName => {
        // Clear for localhost (no domain specified works for localhost)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        
        // Clear for production domains  
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.aristai.io`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=aristai.io`;
        
        if (process.env.REACT_APP_DEV_MODE === 'true') {
          console.log(`🗑️ Clearing cookie: ${cookieName}`);
        }
      });
      
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.log('🍪 Cleared client-side cookies using both cookieService and manual clearing');
      }
    } catch (error) {
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.error('❌ Error clearing cookies:', error);
      }
    }

    // Clear all timers and local state
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      setRefreshTimer(null);
    }
    if (logoutWarningTimer) {
      clearTimeout(logoutWarningTimer);
      setLogoutWarningTimer(null);
    }
    setIsRefreshing(false);
    setGracefulLogoutScheduled(false);
    setRefreshQueue([]);
    setUser(null);
    setIdToken(null);
    notificationService.clearAll();
    
    // Clear any client-side storage
    try {
      localStorage.removeItem('test_auth_user');
      sessionStorage.clear(); // Clear any session storage
      if (window.mockAuthUser) {
        delete window.mockAuthUser;
      }
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.log('🗑️ Cleared localStorage and sessionStorage');
      }
    } catch (error) {
      if (process.env.REACT_APP_DEV_MODE === 'true') {
        console.error('❌ Error clearing storage:', error);
      }
    }

    // Redirect to login
    const currentUrl = window.location.href;
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://login.aristai.io';
    const loginUrl = `${authUrl}/auth/signin?to=${encodeURIComponent(currentUrl)}`;
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      console.log('🔄 Redirecting to login...');
    }
    window.location.href = loginUrl;
  };

  const redirectToLogin = () => {
    const currentUrl = window.location.href;
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://login.aristai.io';
    
    // For testing: Use main domain to see if cookies get set
    const testUrl = currentUrl.includes('localhost') 
      ? 'https://mapper.aristai.io' 
      : currentUrl;
    
    const loginUrl = `${authUrl}/auth/signin?to=${encodeURIComponent(testUrl)}`;
    console.log('🔄 Login URL:', loginUrl);
    window.location.href = loginUrl;
  };

  const value = {
    user,
    loading,
    idToken,
    logout,
    refreshToken,
    manualRefreshToken,
    redirectToLogin,
    checkAuthStatus,
    checkTokenExpiry,
    cancelGracefulLogout,
    isRefreshing,
    gracefulLogoutScheduled,
    isCheckingAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};